import { formatTelephoneNumber } from "@phonero/pux-core"
import {
  PuxButton,
  PuxIcon,
  PuxItem,
  PuxSheet,
  PuxSkeletonText,
  PuxText,
  PuxTitle,
  PuxTypography,
  useBreakpoint,
} from "@phonero/pux-react"
import { sheetStyles } from "../../appConstants"
import {
  ActionButtons,
  dataCy,
  num,
  useTokenData,
  InlineError,
  useAppTranslations,
  useAppQuery,
} from "@phonero/common-ux"
import { PageContent } from "../../components/layout"
import React, { FC } from "react"
import {
  SubscriptionInfoDocument,
  SubscriptionInfoQuery,
} from "./SubscriptionQueries.generated"
import {
  arrowMore,
  datacard,
  simCard,
  smartWatch,
  twinSim,
} from "@phonero/pux-icons/icons"
import { useHistory, useRouteMatch } from "react-router-dom"
import css from "./SubscriptionPage.module.scss"
import { ApiProductState, SimCardType2 } from "@phonero/common-graphql/types"
import { OrderNewSimCardsButton } from "./components/OrderNewSimCardsButton"
import { SimCardFragment } from "@phonero/common-ux"
import { useFeatureIsOn } from "@growthbook/growthbook-react"

function DetailBlock({
  text,
  header,
  loading,
  width = 200,
}: {
  text?: React.ReactNode | null | undefined | false
  header: string
  loading: boolean
  width?: number
}) {
  if (!text && !loading) {
    return null
  }
  if (!text) {
    return (
      <div>
        <h4>{header}</h4>
        <PuxSkeletonText animated={loading} style={{ width }} />
      </div>
    )
  }
  return (
    <div>
      <h4>{header}</h4>
      <PuxText>{text}</PuxText>
    </div>
  )
}

export const SubscriptionPage: FC<any> = () => {
  const { i18n, t } = useAppTranslations()
  const { subscriptionId } = useTokenData()
  const orderNewSimCondition = useFeatureIsOn("dpw-order-new-sim")
  const isLaptopFloor = useBreakpoint("lg")
  const { data, error, loading, refetch } = useAppQuery(
    SubscriptionInfoDocument,
    {
      variables: { subscriptionId: String(subscriptionId) },
    }
  )

  const dataOrLoading = !!data || !!loading
  const history = useHistory()
  const match = useRouteMatch<any>({
    path: "/:customerId/:page?/",
  })
  const customerId = match && match.params.customerId

  const noOfDatacards = countSimCards(data, SimCardType2.Datacard)
  const noOfTwinCards = countSimCards(data, SimCardType2.Twin)
  const noOfSmartwatchCards = countSimCards(data, SimCardType2.Smartwatch)

/*  useEffect(() => {
    refetch()
  }, [refetch, data])*/

  return (
    <PageContent refresh={refetch}>
      <InlineError error={error} code="getSubscription" />
      <PuxSheet color="light" style={{ paddingTop: 0, ...sheetStyles }}>
        {dataOrLoading && (
          <PuxItem
            style={{ marginBottom: isLaptopFloor ? "3rem" : "2rem" }}
            activeColor="dark"
            {...dataCy("expand-subscription-details")}
          >
            {isLaptopFloor && (
              <PuxText style={{ fontWeight: "bold" }}>
                {data?.mobileSubscription?.msisdn ? (
                  formatTelephoneNumber(data?.mobileSubscription?.msisdn)
                    .formatted
                ) : (
                  <PuxSkeletonText animated={loading} style={{ width: 200 }} />
                )}
              </PuxText>
            )}
            <PuxTitle
              style={{
                display: "contents",
                fontSize: "1.7rem",
                fontWeight: 700,
              }}
            >
              {data?.mobileSubscription?.name}{" "}
              {!!data?.mobileSubscription?.dataPackageSize &&
                i18n.format(
                  num(data?.mobileSubscription?.dataPackageSize) * 1000000,
                  "0b"
                )}
            </PuxTitle>

            <PuxSheet slot="detail" color="medium">
              <div className="detailBlock">
                <DetailBlock
                  text={
                    data?.mobileSubscription?.includedDescription ||
                    `${Math.round(
                      (data?.mobileSubscription?.dataPackageSize || 0) / 1000
                    )} GB data inkludert i Norge og EU/EØS`
                  }
                  header={t("Subscription.About")}
                  loading={loading}
                />
                <DetailBlock
                  text={data?.mobileSubscription?.userName}
                  header={t("Subscription.UserName")}
                  loading={loading}
                />
                <DetailBlock
                  text={data?.mobileSubscription?.customer?.name}
                  header={t("Subscription.CustomerName")}
                  loading={loading}
                />
              </div>
            </PuxSheet>
          </PuxItem>
        )}
        <ActionButtons />
      </PuxSheet>
      <PuxSheet
        {...dataCy("sim-cards")}
        style={{
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          ...sheetStyles,
          borderTop: "1px solid #ccc",
          paddingBottom: 50,
          paddingTop: 10,
        }}
        color={"light"}
      >
        {" "}
        <div className={css.simCards}>
          <PuxTypography
            variant="subtitle2"
            style={{ fontWeight: 900, padding: 20 }}
          >
            {t("Subscription.MySimCards")}
          </PuxTypography>
          <div>{orderNewSimCondition && <OrderNewSimCardsButton />}</div>
          <div>            
            <PuxButton
              type="button"
              color="medium"
              expand="full"
              size="large"
              className={css.simCardTypeButton}
              onClick={() => history.push(`${customerId}/hovedsim`)}
            >
              <PuxIcon icon={simCard} color={"primary"} />
              <div className={css.simCardName}>
                {t("Subscription.PrimarySimCard")}
              </div>
              <span slot="end" className={css.simCardCount}>
                <PuxIcon
                  icon={arrowMore}
                  size="small"
                  className={css.arrowMoreIcon}
                />
              </span>
            </PuxButton>
            <PuxButton
              type="button"
              disabled={noOfDatacards === 0}
              color="medium"
              expand="full"
              size="large"
              className={css.simCardTypeButton}
              onClick={() => history.push(`${customerId}/datasim`)}
            >
              <div slot="start">
                <PuxIcon icon={datacard} color={"primary"} />
              </div>
              <div className={css.simCardName}>
                {t("Subscription.DataSimCard")}
              </div>
              <span slot="end" className={css.simCardCount}>
                {noOfDatacards}
                <span>stk.</span>
                {noOfDatacards !== 0 ? (
                  noOfDatacards && (
                    <PuxIcon
                      icon={arrowMore}
                      size="small"
                      className={css.arrowMoreIcon}
                    />
                  )
                ) : (
                  <div style={{ marginLeft: "4rem" }}></div>
                )}
              </span>
            </PuxButton>
            <PuxButton
              type="button"
              disabled={noOfTwinCards === 0}
              color="medium"
              expand="full"
              size="large"
              onClick={() => history.push(`${customerId}/tvillingsim`)}
              className={css.simCardTypeButton}
            >
              <div slot="start">
                <PuxIcon icon={twinSim} color={"primary"} />
              </div>
              <div className={css.simCardName}>
                {t("Subscription.TwinSimCard")}
              </div>
              <span slot="end" className={css.simCardCount}>
                <>{noOfTwinCards}</>
                <span>stk.</span>
                {noOfTwinCards !== 0 ? (
                  noOfTwinCards && (
                    <PuxIcon
                      icon={arrowMore}
                      size="small"
                      className={css.arrowMoreIcon}
                    />
                  )
                ) : (
                  <div style={{ marginLeft: "4rem" }}></div>
                )}
              </span>
            </PuxButton>
            {noOfSmartwatchCards > 0 && (
              <PuxButton
                type="button"
                disabled={noOfSmartwatchCards === 0}
                color="medium"
                expand="full"
                size="large"
                onClick={() => history.push(`${customerId}/smartklokke`)}
                className={css.simCardTypeButton}
              >
                <div slot="start">
                  <PuxIcon icon={smartWatch} color={"primary"} />
                </div>
                <div className={css.simCardName}>
                  {t("Subscription.SmartWatchSimCard")}
                </div>
                <span slot="end" className={css.simCardCount}>
                  <>{noOfSmartwatchCards}</>
                  <span>stk.</span>
                  {noOfSmartwatchCards !== 0 ? (
                    noOfSmartwatchCards && (
                      <PuxIcon
                        icon={arrowMore}
                        size="small"
                        className={css.arrowMoreIcon}
                      />
                    )
                  ) : (
                    <div style={{ marginLeft: "4rem" }}></div>
                  )}
                </span>
              </PuxButton>
            )}
          </div>
        </div>
      </PuxSheet>
    </PageContent>
  )
}

export default SubscriptionPage

function countSimCards(
  data: SubscriptionInfoQuery | undefined,
  simcardType: SimCardType2
) {
  const cards =
    (data?.mobileSubscription?.products?.filter((c) => {
      const simCard = c
      return (
        simCard?.state !== ApiProductState.Available &&
        "type" in simCard && simCard?.type === simcardType
      )
    })) ?? []

  return cards.length
}
